import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import apiClient from '@/lib/axios';
import { useAuth } from '@/components/auth/AuthContext';
import { useUIStore } from '@/stores/uiStore';

const SECURITY_BADGES = {
  public:     'bg-emerald-50 text-emerald-700 border border-emerald-200',
  obfuscated: 'bg-amber-50 text-amber-700 border border-amber-200',
  private:    'bg-red-50 text-[#003cc5] border border-red-200',
};

function resolveIncluded(included, rel) {
  if (!rel?.data?.id) return null;
  const match = included.find((r) => r.id === rel.data.id);
  return match?.attributes?.name ?? null;
}

function useMyCatalysts(uid) {
  return useQuery({
    queryKey: ['myCatalysts', uid],
    queryFn: async ({ signal }) => {
      const { data: res } = await apiClient.get(
        '/jsonapi/impact_catalyst/impact_catalyst',
        {
          signal,
          headers: { Accept: 'application/vnd.api+json' },
          params: {
            include: 'field_security_level,field_event_type,field_organization',
            'filter[uid.drupal_internal__uid]': uid,
            'sort': '-created',
          },
        },
      );

      const included = res.included ?? [];

      return (res.data ?? []).map((item) => {
        const a = item.attributes;
        const geo = a.field_geofield;
        const securityName = resolveIncluded(included, item.relationships?.field_security_level);
        const eventTypeName = resolveIncluded(included, item.relationships?.field_event_type);

        return {
          uuid:           item.id,
          impact_count:   a.field_impact_count,
          event_date:     a.field_event_date,
          region:         a.field_region,
          lives_affected: a.field_lives_affected,
          narrative:      a.field_narrative?.processed ?? null,
          story:          a.field_story ?? null,
          security_level: securityName?.toLowerCase() ?? 'public',
          event_type:     eventTypeName ?? null,
          lat:            geo?.lat ?? null,
          lon:            geo?.lon ?? null,
        };
      });
    },
    enabled: !!uid,
    staleTime: 60_000,
  });
}

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex gap-3">
      <span className="w-32 shrink-0 text-xs text-neutral-400">{label}</span>
      <span className="text-xs text-ink break-words">{value}</span>
    </div>
  );
}

function CatalystCard({ c }) {
  const [expanded, setExpanded] = useState(false);

  const coordLabel =
    c.lat != null && c.lon != null
      ? `${c.lat.toFixed(6)}, ${c.lon.toFixed(6)}`
      : null;

  return (
    <div className="rounded-xl border border-neutral-200 bg-paper animate-fade-in overflow-hidden">
      {/* Summary row — always visible, clickable */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-5 py-4 text-left"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-ink">
                {c.impact_count ?? 0} impacted
              </span>
              {c.lives_affected != null && (
                <span className="text-xs text-neutral-500">
                  · {c.lives_affected} lives affected
                </span>
              )}
            </div>

            <p className="mt-0.5 text-xs text-neutral-500">
              {c.region ? `${c.region} · ` : ''}
              {coordLabel ?? 'Location hidden'}
              {c.event_date
                ? ` · ${format(parseISO(c.event_date), 'MMM d, yyyy')}`
                : ''}
            </p>

            {c.event_type && (
              <p className="mt-1 text-xs text-neutral-500 italic">{c.event_type}</p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                SECURITY_BADGES[c.security_level] ?? 'bg-paper text-neutral-500'
              }`}
            >
              {c.security_level}
            </span>
            <svg
              className={`h-4 w-4 text-neutral-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Narrative preview when collapsed */}
        {!expanded && c.narrative && c.security_level !== 'private' && (
          <p
            className="mt-2 text-xs text-neutral-500 line-clamp-2 text-left"
            dangerouslySetInnerHTML={{ __html: c.narrative }}
          />
        )}
        {!expanded && c.security_level === 'private' && (
          <p className="mt-2 text-xs text-amber-600 text-left">
            Private — narrative encrypted, coordinates hidden
          </p>
        )}
        {!expanded && c.security_level === 'obfuscated' && (
          <p className="mt-2 text-xs text-amber-500 text-left">
            Obfuscated — coordinates fuzzed on public map
          </p>
        )}
      </button>

      {/* Expanded detail panel */}
      {expanded && (
        <div className="border-t border-neutral-200 bg-document px-5 py-4 space-y-2.5">
          <DetailRow label="UUID" value={c.uuid} />
          <DetailRow label="Event type" value={c.event_type} />
          <DetailRow label="Security" value={c.security_level} />
          <DetailRow label="Impact count" value={c.impact_count} />
          <DetailRow label="Lives affected" value={c.lives_affected} />
          <DetailRow label="Region" value={c.region} />
          <DetailRow
            label="Coordinates"
            value={coordLabel ?? (c.security_level === 'private' ? 'Hidden (private)' : 'Not set')}
          />
          <DetailRow
            label="Event date"
            value={c.event_date ? format(parseISO(c.event_date), 'PPP') : null}
          />

          {c.security_level === 'private' ? (
            <div className="pt-1">
              <p className="text-xs text-amber-600">
                Private — narrative is AES-256-GCM encrypted and coordinates are hidden.
              </p>
            </div>
          ) : (
            <>
              {c.narrative && (
                <div className="pt-1">
                  <p className="mb-1 text-xs text-neutral-400">Narrative</p>
                  <div
                    className="prose prose-xs max-w-none text-xs text-ink"
                    dangerouslySetInnerHTML={{ __html: c.narrative }}
                  />
                </div>
              )}
              {c.story && (
                <div className="pt-1">
                  <p className="mb-1 text-xs text-neutral-400">Testimony</p>
                  <p className="text-xs text-ink whitespace-pre-wrap">{c.story}</p>
                </div>
              )}
            </>
          )}

          {c.security_level === 'obfuscated' && (
            <p className="pt-1 text-xs text-amber-500">
              Obfuscated — coordinates shown here are precise, but fuzzed ~10 km on the public map.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ContributorDashboard() {
  const { user } = useAuth();
  const { data: catalysts = [], isLoading } = useMyCatalysts(user?.id);
  const openContributorPortal = useUIStore((s) => s.openContributorPortal);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink">My Catalysts</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {catalysts.length} event{catalysts.length !== 1 ? 's' : ''} submitted
          </p>
        </div>
        <button
          onClick={openContributorPortal}
          className="rounded-xl bg-[#003cc5] px-4 py-2 text-sm font-semibold text-parchment transition hover:bg-[#0036b1]"
        >
          + New Catalyst
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-skeleton rounded-xl bg-paper" />
          ))}
        </div>
      ) : catalysts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-200 py-20 text-center">
          <p className="text-neutral-500">No catalysts submitted yet.</p>
          <button
            onClick={openContributorPortal}
            className="mt-3 text-sm text-[#003cc5] hover:underline"
          >
            Submit your first catalyst →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {catalysts.map((c) => <CatalystCard key={c.uuid} c={c} />)}
        </div>
      )}
    </div>
  );
}
